import { useNavigate, Link } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { finishedSessions, exerciseMap, getLastGymId } from '../../db/repo';
import { weeklyVolume, isoWeekRange, VOLUME_RANGE } from '../../domain/volume';
import { buildInsights, type ExerciseInsight, type Trend } from '../../domain/insights';
import { gymLabel } from '../../domain/gym';
import { MUSCLE_LABEL } from '../../domain/types';
import { todayISO } from '../../lib/dates';

function trendChip(t: Trend) {
  if (t === 'up') return <span className="chip good">up</span>;
  if (t === 'down') return <span className="chip bad">down</span>;
  if (t === 'stalled') return <span className="chip warn">stalled</span>;
  if (t === 'flat') return <span className="chip">flat</span>;
  return <span className="chip">new</span>;
}

function InsightRow({ i, onClick }: { i: ExerciseInsight; onClick: () => void }) {
  return (
    <button className="list-tap" onClick={onClick}>
      <div>
        <strong>{i.name}</strong>
        <div className="faint" style={{ fontSize: 12 }}>
          {MUSCLE_LABEL[i.muscle]} - {i.sessionCount} session{i.sessionCount > 1 ? 's' : ''}
        </div>
      </div>
      <div className="row" style={{ gap: 8 }}>
        {i.deltaPct != null && Math.abs(i.deltaPct) >= 0.5 && (
          <span className="faint" style={{ fontSize: 12 }}>{i.deltaPct > 0 ? '+' : ''}{i.deltaPct.toFixed(0)}%</span>
        )}
        {trendChip(i.trend)}
      </div>
    </button>
  );
}

export function AnalysisScreen() {
  const navigate = useNavigate();
  const data = useLiveQuery(async () => {
    const [sessions, exById, gymId] = await Promise.all([finishedSessions(), exerciseMap(), getLastGymId()]);
    const week = isoWeekRange(todayISO());
    return {
      // Volume is deliberately cross-gym: a hard set counts wherever it happened.
      volume: weeklyVolume(sessions, exById, week.start, week.end),
      // Trends are NOT: this reuses the engine's stall rule, so mixing two loading
      // scales would flag stalls on the gym alternating rather than on a plateau.
      insights: buildInsights(sessions, exById, gymId),
      gymId,
    };
  }, []);

  if (!data) return <div className="screen"><div className="empty">Loading...</div></div>;
  const { volume, insights, gymId } = data;

  if (insights.length === 0) {
    return (
      <div className="screen">
        <div className="screen-title">Analysis</div>
        <div className="empty">No finished sessions at {gymLabel(gymId)} yet. Log a few and your trends show up here.</div>
      </div>
    );
  }

  const progressing = insights.filter((i) => i.trend === 'up');
  const flagged = insights.filter((i) => i.trend === 'stalled' || i.trend === 'down');
  const underTarget = volume.byMuscle.filter(
    (m) => m.frequencyTarget != null && (!m.meetsFrequency || m.workingSets < VOLUME_RANGE.low),
  );

  return (
    <div className="screen">
      <div className="screen-title">Analysis</div>

      <div className="section-label">This week</div>
      <Link className="list-tap" to="/more/volume">
        <div>
          <div className="row" style={{ gap: 8, alignItems: 'baseline' }}>
            <span className="kpi" style={{ fontSize: 22 }}>{volume.totalWorkingSets}</span>
            <span className="faint" style={{ fontSize: 13 }}>working sets ({VOLUME_RANGE.low}-{VOLUME_RANGE.high}/muscle)</span>
          </div>
          <div className="faint" style={{ fontSize: 12, marginTop: 2 }}>
            {underTarget.length > 0
              ? `Under target: ${underTarget.map((m) => MUSCLE_LABEL[m.muscle]).slice(0, 4).join(', ')}`
              : 'All tracked muscles on target'}
          </div>
        </div>
        <span className="chip">Breakdown</span>
      </Link>

      <div className="faint" style={{ fontSize: 12, marginTop: 14 }}>
        Trends below are {gymLabel(gymId)} only - loads aren't comparable across gyms.
        Weekly volume above counts both.
      </div>

      {progressing.length > 0 && (
        <>
          <div className="section-label">Moving well</div>
          {progressing.slice(0, 6).map((i) => (
            <InsightRow key={i.exerciseId} i={i} onClick={() => navigate(`/analysis/${i.exerciseId}`)} />
          ))}
        </>
      )}

      {flagged.length > 0 && (
        <>
          <div className="section-label">Watch / stalling</div>
          {flagged.slice(0, 6).map((i) => (
            <InsightRow key={i.exerciseId} i={i} onClick={() => navigate(`/analysis/${i.exerciseId}`)} />
          ))}
        </>
      )}

      <div className="section-label">All exercises</div>
      {insights.map((i) => (
        <InsightRow key={i.exerciseId} i={i} onClick={() => navigate(`/analysis/${i.exerciseId}`)} />
      ))}
    </div>
  );
}
